import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { obterResultadosPainel } from "../services/api";

/**
 * RESULTADOS DAS VAGAS (19/09/2026). O que o eaiRH fez pelas vagas da empresa: quantas pessoas abriram cada vaga,
 * quantas foram se candidatar (aqui ou no site da empresa), quantas receberam retorno — e a evolução por dia.
 *
 * Existe porque a empresa não tinha como ver isso, e é o número dela que a convence a se engajar (responder,
 * dar retorno). Os dados já existiam; só não chegavam a ela.
 */
export default function ResultadosEmpresa() {
  const [dias, setDias] = useState(30);
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => {
    setDados(null); setErro("");
    obterResultadosPainel(dias).then(setDados).catch((e) => setErro(e.message));
  }, [dias]);

  if (erro) return <p role="alert" className="text-sm text-red-600">{erro}</p>;

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          {dados ? <>Suas vagas no eaiRH nos últimos <b>{dados.dias} dias</b> · {dados.vagasAtivas} vaga(s) no ar agora</> : "Carregando..."}
        </p>
        <div className="flex gap-2">
          {[7, 30, 90].map((n) => (
            <button key={n} onClick={() => setDias(n)}
              className={`rounded-full px-3 py-1 text-sm font-semibold border ${dias === n
                ? "bg-primary text-white border-primary" : "bg-white text-gray-600 border-gray-200 hover:text-gray-900"}`}>
              {n} dias
            </button>
          ))}
        </div>
      </div>

      {dados && (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card v={dados.totais.visualizacoes} l="visualizações das vagas" cor="text-gray-800" />
          <Card v={dados.totais.candidaturas + dados.totais.cliquesCandidatar} l="foram se candidatar" cor="text-primary" />
          <Card v={dados.totais.respondidas} l="candidaturas com retorno" cor="text-green-600" />
          <Card v={dados.totais.interesses} l="demonstraram interesse" cor="text-blue-600" />
        </div>

        <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold text-gray-900">Dia a dia</h2>
            <div className="flex gap-4 text-xs text-gray-600">
              <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm bg-gray-300" /> visualizações</span>
              <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" /> foram se candidatar</span>
            </div>
          </div>
          <Serie serie={dados.serie} />
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white">
          <h2 className="border-b border-gray-100 px-4 py-3 font-semibold text-gray-900">Por vaga</h2>
          {dados.vagas.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500">Nenhuma vaga teve movimento neste período.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2 font-semibold">Vaga</th>
                    <th className="px-3 py-2 text-right font-semibold">Visualizações</th>
                    <th className="px-3 py-2 text-right font-semibold">Foram se candidatar</th>
                    <th className="px-3 py-2 text-right font-semibold">Com retorno</th>
                    <th className="px-3 py-2 text-right font-semibold">Interesse</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {dados.vagas.map((v) => (
                    <tr key={v.id}>
                      <td className="px-4 py-2">
                        <Link to={`/vagas/${v.id}`} className="font-medium text-gray-900 hover:text-primary">{v.titulo}</Link>
                        <div className="text-xs text-gray-500">{v.cidade || "—"}{!v.ativa && " · fora do ar"}</div>
                      </td>
                      <td className="px-3 py-2 text-right">{v.visualizacoes}</td>
                      <td className="px-3 py-2 text-right font-semibold text-primary">{v.candidaturas + v.cliquesCandidatar}</td>
                      <td className="px-3 py-2 text-right text-green-700">{v.respondidas}</td>
                      <td className="px-3 py-2 text-right">{v.interesses}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="mt-4 text-xs text-gray-500 leading-relaxed">
          "Foram se candidatar" soma quem se candidatou pelo eaiRH e quem clicou para se candidatar no site da sua
          empresa. As visualizações de quem já tem conta no eaiRH passaram a ser contadas em 19/09/2026 — antes
          disso, o número mostra só os visitantes sem conta. Nenhum dado pessoal de quem visualizou é registrado.
        </p>
      </>)}
    </>
  );
}

function Card({ v, l, cor }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 text-center">
      <div className={`text-2xl font-bold ${cor}`} style={{ fontVariantNumeric: "tabular-nums" }}>{v.toLocaleString("pt-BR")}</div>
      <div className="text-xs text-gray-500">{l}</div>
    </div>
  );
}

// Barras por dia: visualizações ao fundo (cinza) e quem foi se candidatar na frente (cor da marca), na MESMA
// escala — assim a proporção entre as duas é a que se vê. O rótulo do eixo mostra só o máximo e o zero.
function Serie({ serie }) {
  const max = Math.max(1, ...serie.map((s) => Math.max(s.visualizacoes, s.candidaturas)));
  const W = 600, H = 140, base = H - 18, larg = W / serie.length;
  const y = (n) => base - (n / max) * (base - 8);
  const dm = (iso) => { const [, m, d] = iso.split("-"); return `${d}/${m}`; };
  const marcas = serie.length <= 7 ? serie.map((_, i) => i) : [0, Math.floor(serie.length / 2), serie.length - 1];
  return (
    <svg viewBox={`0 0 ${W + 34} ${H}`} className="w-full h-auto" role="img"
      aria-label={`Visualizações e candidaturas por dia; máximo de ${max} num dia`}>
      <line x1="34" x2={W + 34} y1={base} y2={base} stroke="#e5e7eb" />
      <text x="28" y={y(max) + 4} textAnchor="end" fontSize="10" fill="#6b7280">{max}</text>
      <text x="28" y={base + 4} textAnchor="end" fontSize="10" fill="#6b7280">0</text>
      {serie.map((s, i) => {
        const x = 34 + i * larg + larg * 0.15, w = larg * 0.7;
        return (
          <g key={s.dia}>
            <title>{`${dm(s.dia)}: ${s.visualizacoes} visualizações, ${s.candidaturas} foram se candidatar`}</title>
            <rect x={x} y={y(s.visualizacoes)} width={w} height={base - y(s.visualizacoes)} fill="#d1d5db" rx="1.5" />
            <rect x={x + w * 0.2} y={y(s.candidaturas)} width={w * 0.6} height={base - y(s.candidaturas)} className="fill-primary" rx="1.5" />
          </g>
        );
      })}
      {marcas.map((i) => (
        <text key={i} x={34 + i * larg + larg / 2} y={H - 4} textAnchor="middle" fontSize="10" fill="#6b7280">{dm(serie[i].dia)}</text>
      ))}
    </svg>
  );
}
